import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Search, Loader2, RefreshCw, Pencil } from "lucide-react";
import { useIsAdmin } from "../../../hooks/useIsAdmin";
import { epicengageSupabasev1 } from "../../../integrations/supabase/client";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Card } from "../../ui/card";
import { Switch } from "../../ui/switch";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { useToast } from "../../../hooks/use-toast";
import { cn } from "../../../lib/utils";

type PlanKey = "Foundation" | "Visibility" | "Authority" | "Domination" | "Enterprise";
const PLAN_CREDITS: Record<PlanKey, number | null> = {
  Foundation: 5000,
  Visibility: 20000,
  Authority: 50000,
  Domination: 100000,
  Enterprise: null,
};
const PLAN_OPTIONS: PlanKey[] = ["Foundation", "Visibility", "Authority", "Domination", "Enterprise"];

type MerchantRow = {
  id: string;
  name: string;
  business_type: string | null;
  region: string | null;
  sender_email: string | null;
  sender_domain: string | null;
  ses_verified: boolean | null;
  max_discount_pct: number | null;
  webhook_secret: string | null;
  brand_tone: string | null;
  created_at: string | null;
  owner_email: string | null;
  credits: {
    id: string;
    plan_name: string | null;
    credits_total: number | null;
    credits_used: number | null;
    reset_at: string | null;
  } | null;
};

const fmtDate = (d?: string | null) => (d ? format(new Date(d), "MMM d, yyyy") : "—");

const Admin = () => {
  const { isAdmin, loading } = useIsAdmin();
  const { toast } = useToast();
  const [rows, setRows] = useState<MerchantRow[]>([]);
  const [loadingRows, setLoadingRows] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<MerchantRow | null>(null);

  const fetchData = async () => {
    setLoadingRows(true);
    const { data, error } = await epicengageSupabasev1.functions.invoke("admin-merchants");
    if (error) {
      toast({ title: "Failed to load merchants", description: error.message, variant: "destructive" });
    } else {
      setRows((data as { merchants: MerchantRow[] }).merchants ?? []);
    }
    setLoadingRows(false);
  };

  useEffect(() => {
    if (isAdmin) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.owner_email?.toLowerCase().includes(q) ||
        r.business_type?.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const updateLocal = (id: string, patch: Partial<MerchantRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const saveCredits = async (
    merchantId: string,
    creditsId: string | undefined,
    patch: { plan_name?: string | null; credits_total?: number | null; credits_used?: number | null; reset_at?: string | null },
  ) => {
    if (creditsId) {
      const { error } = await epicengageSupabasev1.from("email_credits").update(patch).eq("id", creditsId);
      if (error) {
        toast({ title: "Save failed", description: error.message, variant: "destructive" });
        return false;
      }
    } else {
      const { data, error } = await epicengageSupabasev1
        .from("email_credits")
        .insert({ merchant_id: merchantId, ...patch })
        .select()
        .single();
      if (error) {
        toast({ title: "Save failed", description: error.message, variant: "destructive" });
        return false;
      }
      updateLocal(merchantId, { credits: data as MerchantRow["credits"] });
    }
    return true;
  };

  const handlePlanChange = async (row: MerchantRow, plan: PlanKey) => {
    const credits = PLAN_CREDITS[plan];
    const patch = credits != null
      ? { plan_name: plan, credits_total: credits }
      : { plan_name: plan };
    const ok = await saveCredits(row.id, row.credits?.id, patch);
    if (ok) {
      updateLocal(row.id, {
        credits: { ...(row.credits ?? { id: "", credits_used: 0, reset_at: null }), ...patch } as MerchantRow["credits"],
      });
      toast({ title: "Plan updated", description: `${row.name} → ${plan}` });
    }
  };

  const handleCreditsTotal = async (row: MerchantRow, value: number) => {
    const ok = await saveCredits(row.id, row.credits?.id, { credits_total: value });
    if (ok) updateLocal(row.id, { credits: { ...(row.credits ?? { id: "", plan_name: null, credits_used: 0, reset_at: null }), credits_total: value } as MerchantRow["credits"] });
  };

  const handleResetDate = async (row: MerchantRow, date: Date | undefined) => {
    const iso = date ? date.toISOString() : null;
    const ok = await saveCredits(row.id, row.credits?.id, { reset_at: iso });
    if (ok) updateLocal(row.id, { credits: { ...(row.credits ?? { id: "", plan_name: null, credits_total: 0, credits_used: 0 }), reset_at: iso } as MerchantRow["credits"] });
  };

  const handleSesToggle = async (row: MerchantRow, value: boolean) => {
    const { error } = await epicengageSupabasev1.from("merchants").update({ ses_verified: value }).eq("id", row.id);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    updateLocal(row.id, { ses_verified: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <div>
      <div className="px-8 py-6 max-w-[1600px]">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Admin Panel</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage merchant accounts and billing</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loadingRows}>
            <RefreshCw className={cn("h-4 w-4", loadingRows && "animate-spin")} />
            Refresh
          </Button>
        </div>

        <Card className="p-4 mb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search business, email, type…"
              className="pl-9"
            />
          </div>
        </Card>

        <Card className="overflow-hidden">
          {loadingRows ? (
            <div className="p-12 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading merchants…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-muted-foreground">No merchants found.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[140px]">Plan</TableHead>
                  <TableHead className="w-[110px]">Credits Total</TableHead>
                  <TableHead className="w-[90px]">Used</TableHead>
                  <TableHead className="w-[100px]">Remaining</TableHead>
                  <TableHead className="w-[150px]">Reset Date</TableHead>
                  <TableHead className="w-[80px]">SES</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const total = r.credits?.credits_total ?? 0;
                  const used = r.credits?.credits_used ?? 0;
                  const remaining = Math.max(0, total - used);
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.owner_email ?? "—"}</TableCell>
                      <TableCell className="text-sm">{r.business_type ?? "—"}</TableCell>
                      <TableCell>
                        <Select
                          value={(r.credits?.plan_name as PlanKey) ?? ""}
                          onValueChange={(v) => handlePlanChange(r, v as PlanKey)}
                        >
                          <SelectTrigger className="h-8"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {PLAN_OPTIONS.map((p) => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          className="h-8"
                          defaultValue={total}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value || "0", 10);
                            if (v !== total) handleCreditsTotal(r, v);
                          }}
                          style={{width:'100px'}}
                        />
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">{used.toLocaleString()}</TableCell>
                      <TableCell className="text-sm tabular-nums font-medium">{remaining.toLocaleString()}</TableCell>
                      <TableCell>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 w-full justify-start font-normal">
                              <CalendarIcon className="h-3 w-3" />
                              {fmtDate(r.credits?.reset_at)}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={r.credits?.reset_at ? new Date(r.credits.reset_at) : undefined}
                              onSelect={(d) => handleResetDate(r, d)}
                              className={cn("p-3 pointer-events-auto")}
                            />
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <Switch checked={!!r.ses_verified} onCheckedChange={(v) => handleSesToggle(r, v)} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(r)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      {editing && (
        <EditMerchantDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            updateLocal(updated.id, updated);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const EditMerchantDialog = ({
  row,
  onClose,
  onSaved,
}: {
  row: MerchantRow;
  onClose: () => void;
  onSaved: (r: MerchantRow) => void;
}) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(row.name);
  const [businessType, setBusinessType] = useState(row.business_type ?? "");
  const [region, setRegion] = useState(row.region ?? "");
  const [senderEmail, setSenderEmail] = useState(row.sender_email ?? "");
  const [senderDomain, setSenderDomain] = useState(row.sender_domain ?? "");
  const [maxDiscount, setMaxDiscount] = useState(row.max_discount_pct ?? 20);
  const [sesVerified, setSesVerified] = useState(!!row.ses_verified);
  const [webhookSecret, setWebhookSecret] = useState(row.webhook_secret ?? "");
  const [plan, setPlan] = useState<string>(row.credits?.plan_name ?? "");
  const [creditsTotal, setCreditsTotal] = useState<number>(row.credits?.credits_total ?? 0);
  const [resetAt, setResetAt] = useState<Date | undefined>(row.credits?.reset_at ? new Date(row.credits.reset_at) : undefined);

  const adjustCredits = (delta: number) => setCreditsTotal((v) => Math.max(0, (v || 0) + delta));

  const onPlan = (p: string) => {
    setPlan(p);
    const c = PLAN_CREDITS[p as PlanKey];
    if (c != null) setCreditsTotal(c);
  };

  const regenerate = () => {
    setWebhookSecret(crypto.randomUUID());
  };

  const resetUsed = async () => {
    if (!row.credits?.id) return;
    const { error } = await epicengageSupabasev1.from("email_credits").update({ credits_used: 0 }).eq("id", row.credits.id);
    if (error) {
      toast({ title: "Failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Used credits reset to 0" });
    onSaved({ ...row, credits: { ...row.credits, credits_used: 0 } });
  };

  const save = async () => {
    setSaving(true);
    const { error: mErr } = await epicengageSupabasev1
      .from("merchants")
      .update({
        name,
        business_type: businessType || null,
        region: region || null,
        sender_email: senderEmail || null,
        sender_domain: senderDomain || null,
        max_discount_pct: maxDiscount,
        ses_verified: sesVerified,
        webhook_secret: webhookSecret || null,
      })
      .eq("id", row.id);
    if (mErr) {
      toast({ title: "Save failed", description: mErr.message, variant: "destructive" });
      setSaving(false);
      return;
    }

    const creditPatch = { plan_name: plan || null, credits_total: creditsTotal, reset_at: resetAt ? resetAt.toISOString() : null };
    let creditsRow = row.credits;
    if (row.credits?.id) {
      const { error } = await epicengageSupabasev1.from("email_credits").update(creditPatch).eq("id", row.credits.id);
      if (error) {
        toast({ title: "Credits save failed", description: error.message, variant: "destructive" });
        setSaving(false);
        return;
      }
      creditsRow = { ...row.credits, ...creditPatch };
    } else {
      const { data, error } = await epicengageSupabasev1
        .from("email_credits")
        .insert({ merchant_id: row.id, ...creditPatch })
        .select()
        .single();
      if (error) {
        toast({ title: "Credits save failed", description: error.message, variant: "destructive" });
        setSaving(false);
        return;
      }
      creditsRow = data as MerchantRow["credits"];
    }

    toast({ title: "Merchant saved" });
    onSaved({
      ...row,
      name,
      business_type: businessType || null,
      region: region || null,
      sender_email: senderEmail || null,
      sender_domain: senderDomain || null,
      max_discount_pct: maxDiscount,
      ses_verified: sesVerified,
      webhook_secret: webhookSecret || null,
      credits: creditsRow,
    });
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit merchant — {row.name}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Business name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label>Business type</Label>
            <Input value={businessType} onChange={(e) => setBusinessType(e.target.value)} />
          </div>
          <div>
            <Label>Region</Label>
            <Input value={region} onChange={(e) => setRegion(e.target.value)} />
          </div>
          <div>
            <Label>Plan</Label>
            <Select value={plan} onValueChange={onPlan}>
              <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
              <SelectContent>
                {PLAN_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Credits total</Label>
            <Input type="number" value={creditsTotal} onChange={(e) => setCreditsTotal(parseInt(e.target.value || "0", 10))} />
          </div>

          <div className="col-span-2 flex flex-wrap items-center gap-2 -mt-2">
            <span className="text-xs text-muted-foreground mr-2">Quick adjust:</span>
            <Button type="button" size="sm" variant="outline" onClick={() => adjustCredits(500)}>+500</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => adjustCredits(1000)}>+1,000</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => adjustCredits(5000)}>+5,000</Button>
            <Button type="button" size="sm" variant="outline" onClick={resetUsed} className="ml-auto">
              Reset used credits to 0
            </Button>
          </div>

          <div className="col-span-2">
            <Label>Reset date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal">
                  <CalendarIcon className="h-4 w-4" />
                  {resetAt ? format(resetAt, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={resetAt} onSelect={setResetAt} className={cn("p-3 pointer-events-auto")} />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <Label>Sender email</Label>
            <Input value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} />
          </div>
          <div>
            <Label>Sender domain</Label>
            <Input value={senderDomain} onChange={(e) => setSenderDomain(e.target.value)} />
          </div>
          <div>
            <Label>Max discount %</Label>
            <Input type="number" value={maxDiscount} onChange={(e) => setMaxDiscount(parseInt(e.target.value || "0", 10))} />
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={sesVerified} onCheckedChange={setSesVerified} />
            <Label className="!mt-0">SES verified</Label>
          </div>

          <div className="col-span-2">
            <Label>Webhook secret</Label>
            <div className="flex gap-2">
              <Input value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} className="font-mono text-xs" />
              <Button type="button" variant="outline" onClick={regenerate}>Regenerate</Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Admin;
